import type React from 'react'
import { useState } from 'react'
import { ShoppingCart, Plus, Trash2, Download, Copy, Sparkles, X, ChefHat, ChevronDown } from 'lucide-react'
import { shoppingApi, recipesApi, type Recipe, type Dessert, type ShoppingList, type ShoppingListItem } from '../../services/api'
import { exportToPDF, copyToClipboard } from '../../services/export'
import { useAuth } from '../../contexts/useAuth'
import toast from 'react-hot-toast'

interface Props {
    state: {
        name: string
        selectedRecipes: Recipe[]
        desserts: Dessert[]
        nbPersons: number
        durationDays: number
        mealsPerDay: number
    }
    onComplete: (list: ShoppingList) => void
}

export default function Step3Review({ state, onComplete }: Props) {
    const { isAuthenticated } = useAuth()
    const [list, setList] = useState<ShoppingList | null>(null)
    const [generating, setGenerating] = useState(false)
    const [addingItem, setAddingItem] = useState(false)
    const [newItemName, setNewItemName] = useState('')
    const [newItemQty, setNewItemQty] = useState('')
    const [newItemUnit, setNewItemUnit] = useState('g')
    const [showRecipes, setShowRecipes] = useState(false)

    const generate = async () => {
        setGenerating(true)
        try {
            // Save LLM recipes in database first (requires authentication)
            const dbRecipes = [...state.selectedRecipes]
            for (let i = 0; i < dbRecipes.length; i++) {
                const r = dbRecipes[i]
                if (r.id.startsWith('llm-')) {
                    const created = await recipesApi.create({
                        name: r.name,
                        servings: r.servings,
                        is_personal: true,
                        category: 'plat',
                        ingredients: r.recipe_ingredients.map(ri => ({
                            ingredient_name: ri.name,
                            quantity: ri.quantity,
                            unit: ri.unit
                        }))
                    })
                    dbRecipes[i] = created
                }
            }

            const generated = await shoppingApi.generate({
                name: state.name,
                recipe_ids: dbRecipes.map(r => r.id),
                desserts: state.desserts.map(d => ({ recipe_id: d.recipe_id, nb_repetitions: d.nb_repetitions, nb_persons: d.nb_persons })),
                nb_persons: state.nbPersons,
                duration_days: state.durationDays,
                meals_per_day: state.mealsPerDay,
            })
            setList(generated)
            toast.success('Liste générée !')
        } catch {
            toast.error('Erreur lors de la génération')
        } finally {
            setGenerating(false)
        }
    }

    const toggleCheck = async (item: ShoppingListItem) => {
        if (!list) return
        if (isAuthenticated) {
            const updated = await shoppingApi.updateItem(list.id, item.id, { checked: !item.checked })
            setList((prev: ShoppingList | null) => prev ? { ...prev, items: prev.items.map((i: ShoppingListItem) => i.id === item.id ? { ...i, ...updated } : i) } : prev)
        } else {
            setList((prev: ShoppingList | null) => prev ? { ...prev, items: prev.items.map((i: ShoppingListItem) => i.id === item.id ? { ...i, checked: !i.checked } : i) } : prev)
        }
    }

    const toggleOwned = async (item: ShoppingListItem) => {
        if (!list) return
        if (isAuthenticated) {
            const updated = await shoppingApi.updateItem(list.id, item.id, { owned: !item.owned })
            setList((prev: ShoppingList | null) => prev ? { ...prev, items: prev.items.map((i: ShoppingListItem) => i.id === item.id ? { ...i, ...updated } : i) } : prev)
        } else {
            setList((prev: ShoppingList | null) => prev ? { ...prev, items: prev.items.map((i: ShoppingListItem) => i.id === item.id ? { ...i, owned: !i.owned } : i) } : prev)
        }
    }

    const deleteItem = async (item: ShoppingListItem) => {
        if (!list) return
        if (isAuthenticated) {
            await shoppingApi.deleteItem(list.id, item.id)
        }
        setList((prev: ShoppingList | null) => prev ? { ...prev, items: prev.items.filter((i: ShoppingListItem) => i.id !== item.id) } : prev)
        toast.success('Article supprimé')
    }

    const addItem = async () => {
        if (!list || !newItemName.trim() || !newItemQty) return
        if (isAuthenticated) {
            const added = await shoppingApi.addItem(list.id, { name: newItemName.trim(), required_quantity: parseFloat(newItemQty), unit: newItemUnit })
            setList((prev: ShoppingList | null) => prev ? { ...prev, items: [...prev.items, added] } : prev)
        } else {
            const newItem: ShoppingListItem = {
                id: crypto.randomUUID(),
                name: newItemName.trim(),
                required_quantity: parseFloat(newItemQty),
                unit: newItemUnit,
                checked: false,
                owned: false,
                ingredient_id: undefined,
                note: ''
            }
            setList((prev: ShoppingList | null) => prev ? { ...prev, items: [...prev.items, newItem] } : prev)
        }
        setNewItemName('')
        setNewItemQty('')
        setAddingItem(false)
        toast.success('Article ajouté')
    }

    const handleCopy = () => {
        if (!list) return
        copyToClipboard(list)
        toast.success('Liste copiée dans le presse-papiers !')
    }

    const fmtQty = (qty: number, unit: string) => {
        const q = qty % 1 === 0 ? qty.toString() : qty.toFixed(1)
        const labels: Record<string, string> = { cnt: 'pièce(s)', g: 'g', ml: 'ml', tbsp: 'cs', tsp: 'cc' }
        return `${q} ${labels[unit] || unit}`
    }

    if (!list) {
        // Preview before generation
        return (
            <div>
                <div className="info-card" style={{ marginBottom: 16, padding: 20 }}>
                    <h3 style={{ marginBottom: 12 }}>Récapitulatif</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.9rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'var(--text-3)' }}>Plats</span>
                            <span>{state.selectedRecipes.map(r => r.name).join(', ')}</span>
                        </div>
                        {state.desserts.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: 'var(--text-3)' }}>Desserts</span>
                                <span>{state.desserts.length} dessert(s)</span>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'var(--text-3)' }}>Personnes</span>
                            <span>{state.nbPersons}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'var(--text-3)' }}>Durée</span>
                            <span>{state.durationDays} jours</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'var(--text-3)' }}>Repas/jour</span>
                            <span>{state.mealsPerDay}</span>
                        </div>
                        <div className="divider" />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                            <span style={{ color: 'var(--text-3)' }}>Portions/plat</span>
                            <span className="gradient-text">
                                {Math.round((state.durationDays * state.mealsPerDay * state.nbPersons) / Math.max(1, state.selectedRecipes.length) * 10) / 10} portions
                            </span>
                        </div>
                    </div>
                </div>

                {state.selectedRecipes.some(r => r.id.startsWith('llm-')) && (
                    <div style={{
                        padding: '10px 14px',
                        background: 'var(--warning-bg)',
                        border: '1px solid rgba(245, 158, 11, 0.25)',
                        borderRadius: 'var(--radius)',
                        marginBottom: 14,
                        fontSize: '0.85rem',
                        color: 'var(--warning)',
                        display: 'flex',
                        gap: 8,
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                    }}>
                        <Sparkles size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>Certains plats ont été trouvés via IA. Leurs ingrédients sont des estimations — veuillez les vérifier.</span>
                    </div>
                )}

                <button className="btn btn-primary btn-full" onClick={generate} disabled={generating} style={{ padding: '16px' }}>
                    <ShoppingCart size={18} />
                    {generating ? 'Calcul en cours…' : 'Générer la liste'}
                </button>
            </div>
        )
    }

    const toGet = list.items.filter(i => !i.owned)
    const inStock = list.items.filter(i => i.owned)

    return (
        <div>
            {/* Action bar */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <button className="btn btn-secondary btn-sm" onClick={handleCopy} style={{ flex: 1 }}>
                    <Copy size={15} /> Copier
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => exportToPDF(list)} style={{ flex: 1 }}>
                    <Download size={15} /> PDF
                </button>
                <button className="btn btn-primary" onClick={() => onComplete(list)} style={{ flex: 2 }}>
                    <ShoppingCart size={15} /> Faire les courses
                </button>
            </div>

            {/* Selected recipes summary */}
            {list.recipes && list.recipes.length > 0 && (
                <div className="card" style={{ marginBottom: 20, padding: '14px 16px' }}>
                    <div
                        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setShowRecipes(!showRecipes)}
                    >
                        <ChefHat size={16} color="var(--primary)" />
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', flex: 1 }}>Plats sélectionnés ({list.recipes.length})</span>
                        <ChevronDown
                            size={16}
                            style={{
                                transform: showRecipes ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s',
                                color: 'var(--text-3)'
                            }}
                        />
                    </div>
                    {showRecipes && (
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div className="divider" style={{ margin: '0' }} />
                            {list.recipes.map((r, i) => (
                                <div key={r.id || i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                    <span style={{ color: 'var(--text)' }}>{r.name}</span>
                                    <span className="badge badge-gray" style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                                        {r.category === 'dessert' ? 'Dessert' : 'Plat'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Items to buy */}
            <p className="section-title">À acheter ({toGet.length})</p>
            {toGet.length === 0 && <p style={{ color: 'var(--text-3)', fontSize: '0.9rem' }}>Vous avez déjà tout !</p>}
            {toGet.map((item: ShoppingListItem, idx: number) => (
                <div
                    key={item.id}
                    className={`list-item ${item.owned ? 'owned' : ''}`}
                    style={{ animation: `slideUp 0.3s ease ${idx * 0.03}s both` }}
                >
                    <button
                        className={`checkbox ${item.checked ? 'checked' : ''}`}
                        onClick={() => toggleCheck(item)}
                    >
                        {item.checked && '✓'}
                    </button>
                    <div className="list-item-info">
                        <div className="list-item-name">{item.name}</div>
                        <div className="list-item-qty">{fmtQty(item.required_quantity, item.unit)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-success btn-icon" onClick={() => toggleOwned(item)} title="Marquer comme possédé">✓</button>
                        <button className="btn btn-sm btn-danger btn-icon" onClick={() => deleteItem(item)} title="Supprimer"><Trash2 size={13} /></button>
                    </div>
                </div>
            ))}

            {/* In stock */}
            {inStock.length > 0 && (
                <>
                    <p className="section-title">Déjà en stock ({inStock.length})</p>
                    {inStock.map((item: ShoppingListItem) => (
                        <div key={item.id} className="list-item owned">
                            <div className="checkbox owned">✓</div>
                            <div className="list-item-info">
                                <div className="list-item-name" style={{ textDecoration: 'line-through', opacity: 0.6 }}>{item.name}</div>
                                <div className="list-item-qty">{fmtQty(item.required_quantity, item.unit)}</div>
                            </div>
                            <button className="btn btn-sm btn-ghost btn-icon" onClick={() => toggleOwned(item)} title="Retirer du stock">
                                <X size={13} />
                            </button>
                        </div>
                    ))}
                </>
            )}

            {/* Add item */}
            <div style={{ marginTop: 16 }}>
                {!addingItem ? (
                    <button className="btn btn-ghost btn-full" onClick={() => setAddingItem(true)}>
                        <Plus size={16} /> Ajouter un article
                    </button>
                ) : (
                    <div className="card fade-in" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <input className="input" placeholder="Nom de l'article" value={newItemName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewItemName(e.target.value)} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <input className="input" type="number" placeholder="Quantité" value={newItemQty} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewItemQty(e.target.value)} />
                            <select className="select" value={newItemUnit} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewItemUnit(e.target.value)}>
                                <option value="g">g</option>
                                <option value="ml">ml</option>
                                <option value="cnt">pièce(s)</option>
                                <option value="tbsp">c. à soupe</option>
                                <option value="tsp">c. à café</option>
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn btn-ghost" onClick={() => setAddingItem(false)} style={{ flex: 1 }}>Annuler</button>
                            <button className="btn btn-primary" onClick={addItem} style={{ flex: 2 }}>Ajouter</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
